// LoginForm.tsx
import { confirmUserRegistrationCode } from 'lib/api';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

interface ApiResponse {
  success: boolean;
  message: string;
}

interface MappedResponse {
  title: string;
  message: string;
  severity: 'success' | 'info' | 'error';
}

type SeverityType = 'success' | 'info' | 'error';

const ERROR_MESSAGES: Record<
  string,
  Omit<MappedResponse, 'severity'> & { severity: SeverityType }
> = {
  'User already registered': {
    title: 'Usuario ya registrado',
    message:
      'El usuario ya está registrado en el sistema y con su cuenta activa.',
    severity: 'info',
  },
  'Server error': {
    title: 'Error activado la cuenta',
    message:
      'Problemas procesando la solicitud, por favor intenta nuevamente en unos minutos. Si el problema persiste, contacta a soporte.',
    severity: 'error',
  },
  default: {
    title: 'Error activado la cuenta',
    message:
      'El código ingresado no es válido. Por favor verifica el enlace o solicita uno nuevo.',
    severity: 'error',
  },
};

const SUCCESS_RESPONSE: MappedResponse = {
  title: '¡Bienvenido a nuestro portal!',
  message:
    'Tu registro ha sido confirmado, te estamos redirigiendo a la página de inicio.',
  severity: 'success',
};

const mapResponse = (response: ApiResponse): MappedResponse => {
  if (response.success) return SUCCESS_RESPONSE;
  return ERROR_MESSAGES[response.message] || ERROR_MESSAGES.default;
};

const ConfirmUserRegistration = () => {
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState<MappedResponse | null>(null);
  const { code } = useParams<{ code: string }>();

  const confirmCodeUser = async () => {
    if (!code) {
      setResponse(mapResponse({ success: false, message: 'default' }));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const apiResponse = await confirmUserRegistrationCode({ code });
      const mappedResponse = mapResponse(apiResponse);
      setResponse(mappedResponse);

      if (apiResponse.success) {
        setTimeout(() => {
          window.location.href = '/';
        }, 4000);
      }
    } catch (error) {
      console.error('Error confirming user registration:', error);
      setResponse(mapResponse({ success: false, message: 'Server error' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void confirmCodeUser();
  }, []);

  if (loading) return <CenteredCircularProgress />;

  return (
    <div style={{ backgroundColor: '#F8FAFC', height: '100vh' }}>
      <div style={{ margin: 'auto', maxWidth: '30%', padding: 40 }}>
        <AlertBanner
          title={response?.title}
          message={response?.message}
          severity={response?.severity}
        />
        {response?.severity !== 'error' && (
          <CustomButton
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => {
              window.location.href = `/`;
            }}
          >
            Iniciar sesión
          </CustomButton>
        )}
      </div>
    </div>
  );
};

export default ConfirmUserRegistration;
